import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Upload } from "lucide-react";
import api from "@/lib/api";
import customerApi, { CUSTOMER_TOKEN, startGoogleLogin, errMsg } from "@/lib/customerApi";

const DRAFT = "dyor_power_study_draft";
const EMPTY = { name: "", company: "", email: "", phone: "", site_address: "", parcel_id: "", acreage: "",
  target_mw: "", utilities: "", brochure_url: "", notes: "" };
const inputCls = "w-full bg-[#0B0F17] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500";

const Field = ({ label, k, form, set, type = "text", required, placeholder, full }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <span className="text-slate-300 text-sm">{label}{required && <span className="text-[#84CC16]"> *</span>}</span>
    <input data-testid={`study-${k}`} type={type} required={required} placeholder={placeholder} value={form[k]}
      step={type === "number" ? "any" : undefined} min={type === "number" ? "0" : undefined}
      onChange={(e) => set({ ...form, [k]: e.target.value })} className={`${inputCls} mt-1.5`} />
  </label>
);

const AccountBlock = ({ me, mode, setMode, password, setPassword, form }) => {
  if (me) return <p className="text-sm text-slate-300" data-testid="study-signed-in">Signed in as <span className="text-white font-semibold">{me.email}</span>. This order will appear in your <Link to="/account" className="text-[#3B82F6] underline">dashboard</Link>.</p>;
  const google = () => { sessionStorage.setItem(DRAFT, JSON.stringify(form)); startGoogleLogin("/power-study"); };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-300">Create an account to track your order and download your reports.</p>
      <div className="flex gap-2 text-sm">
        {[["new", "New account"], ["existing", "I have an account"]].map(([m, l]) => (
          <button type="button" key={m} data-testid={`study-account-${m}`} onClick={() => setMode(m)}
            className={`px-3 py-1.5 rounded-full border transition-colors ${mode === m ? "border-[#84CC16] text-[#84CC16]" : "border-white/10 text-slate-400"}`}>{l}</button>
        ))}
      </div>
      <input data-testid="study-password" type="password" required minLength={mode === "new" ? 8 : 1} value={password}
        onChange={(e) => setPassword(e.target.value)} placeholder={mode === "new" ? "Choose a password (8+ characters)" : "Your password"} className={inputCls} />
      <button type="button" data-testid="study-google-btn" onClick={google}
        className="w-full sm:w-auto border border-white/15 hover:border-blue-500/60 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
        Continue with Google instead
      </button>
    </div>
  );
};

export default function PowerStudyOrderForm({ disclaimer }) {
  const [form, setForm] = useState(() => ({ ...EMPTY, ...JSON.parse(sessionStorage.getItem(DRAFT) || "{}") }));
  const [file, setFile] = useState(null);
  const [agree, setAgree] = useState(false);
  const [me, setMe] = useState(null);
  const [mode, setMode] = useState("new");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(CUSTOMER_TOKEN)) return;
    customerApi.get("/auth/me").then((r) => {
      setMe(r.data);
      setForm((f) => ({ ...f, email: f.email || r.data.email, name: f.name || r.data.name || "" }));
    }).catch(() => localStorage.removeItem(CUSTOMER_TOKEN));
  }, []);

  const ensureAccount = async () => {
    if (me) return;
    const { data } = mode === "new"
      ? await api.post("/customer/register", { name: form.name, email: form.email, password })
      : await api.post("/auth/login", { email: form.email, password });
    localStorage.setItem(CUSTOMER_TOKEN, data.token);
    setMe(data.user);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.site_address.trim() && !form.parcel_id.trim()) return toast.error("Enter a site address or parcel ID");
    if (!agree) return toast.error("Please agree to the disclaimer before paying");
    setBusy(true);
    try {
      await ensureAccount();
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("agree_disclaimer", "true");
      fd.append("origin_url", window.location.origin);
      if (file) fd.append("brochure", file);
      const { data } = await customerApi.post("/power-study/orders", fd);
      sessionStorage.removeItem(DRAFT);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err, "Could not start your order"));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="power-study-order-form" className="card p-6 sm:p-10">
      <p className="eyebrow mb-2">Order form</p>
      <h2 className="font-display font-bold text-2xl sm:text-3xl text-white">Order a DYOR Power Feasibility Study</h2>
      <p className="text-slate-400 mt-2 text-sm">Study price $5,000. Pay a $1,500 deposit now through Stripe; the $3,500 balance is invoiced when your report is ready.</p>

      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <Field label="Name" k="name" form={form} set={setForm} required />
        <Field label="Company" k="company" form={form} set={setForm} />
        <Field label="Email" k="email" type="email" form={form} set={setForm} required />
        <Field label="Phone" k="phone" type="tel" form={form} set={setForm} required />
        <Field label="Site address" k="site_address" form={form} set={setForm} placeholder="Street, city, state" />
        <Field label="or Parcel ID" k="parcel_id" form={form} set={setForm} placeholder="County parcel / APN" />
        <Field label="Acreage" k="acreage" type="number" form={form} set={setForm} required />
        <Field label="Target MW" k="target_mw" type="number" form={form} set={setForm} required />
        <Field label="Utility or utilities (if known)" k="utilities" form={form} set={setForm} full placeholder="e.g. Georgia Power, Jackson EMC" />
        <Field label="Listing or brochure link" k="brochure_url" type="url" form={form} set={setForm} placeholder="https://" />
        <label className="block">
          <span className="text-slate-300 text-sm">or upload listing/brochure</span>
          <span className={`${inputCls} mt-1.5 flex items-center gap-2 cursor-pointer`}>
            <Upload size={15} className="text-slate-400" />
            <span className="truncate text-slate-400">{file ? file.name : "PDF, Word, Excel, or image (max 25 MB)"}</span>
            <input data-testid="study-brochure-file" type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </span>
        </label>
        <label className="block sm:col-span-2">
          <span className="text-slate-300 text-sm">Notes</span>
          <textarea data-testid="study-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} mt-1.5`} />
        </label>
      </div>

      <div className="border-t border-white/10 mt-8 pt-6">
        <AccountBlock me={me} mode={mode} setMode={setMode} password={password} setPassword={setPassword} form={form} />
      </div>

      <label className="flex items-start gap-3 mt-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-4 cursor-pointer">
        <input data-testid="study-agree-checkbox" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 accent-[#84CC16] w-4 h-4" />
        <span className="text-amber-100 text-sm leading-relaxed">I understand and agree: {disclaimer}</span>
      </label>

      <button type="submit" disabled={busy || !agree} data-testid="study-submit-btn"
        className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#84CC16] hover:bg-[#9ee325] disabled:opacity-50 disabled:cursor-not-allowed text-[#0B0F17] font-bold px-7 py-3.5 rounded-full transition-colors">
        {busy ? <Loader2 size={17} className="animate-spin" /> : <Lock size={16} />} Pay $1,500 deposit securely
      </button>
    </form>
  );
}
